<template>
    <bc-dialog
        :value="entity_phone_dialog"
        :content-class="{ 'd-none': !object_ref }"
        :hide-overlay="!object_ref"
    >
        <v-card>
            <v-card-title>
                <span class="headline">{{ entity_phone.entity_phones_id ? hasScope('update') ? $t('lang.editNumber') : $t('lang.entity_phone_details') : $t('lang.addNumber') }}</span>
                <v-spacer></v-spacer>
                <bc-btn-dialog-close
                    @invoke-method="invokeMethod($event)"
                    :dialog-status="entity_phone_dialog"
                    action="close"
                ></bc-btn-dialog-close>
            </v-card-title>
            <v-card-text>
                <v-form ref="entity_phone_form">
                    <v-container grid-list-form>
                        <v-layout row wrap>
                            <v-flex xs12 pr-0>
                                <v-layout row>
                                    <v-flex xs6 class="primary-chip-container inner-flex">
                                        <v-chip
                                            v-if="entity_phone.prime == '1'"
                                            color="primary"
                                            class="primary-chip"
                                            disabled
                                        >
                                            {{ $t('lang.primary_caps') }}
                                        </v-chip>
                                        <v-switch
                                            v-else
                                            v-model="entity_phone.prime"
                                            :label="$t('lang.primary')"
                                            true-value="1"
                                            false-value="0"
                                        ></v-switch>
                                    </v-flex>
                                    <bc-btn-entity-action
                                        v-if="entity_phone.call_link"
                                        icon="phone_forwarded"
                                        :text="$t('lang.call')"
                                        :currentEntity="entity_phone"
                                        :link="entity_phone.call_link"
                                    ></bc-btn-entity-action>
                                    <bc-btn-entity-action
                                        v-if="entity_phone.sms_link"
                                        icon="chat"
                                        :text="$t('lang.message')"
                                        :currentEntity="entity_phone"
                                        :link="entity_phone.sms_link"
                                    ></bc-btn-entity-action>
                                </v-layout>
                            </v-flex>
                            <v-flex xs12>
                                <bc-phone-input
                                    v-focus="entity_phone_dialog"
                                    ref="phoneInput"
                                    v-model="entity_phone.phone_number"
                                    :phone_number="entity_phone.phone_number"
                                    :code="entity_phone.country_iso_2"
                                    :updateObject="entity_phone"
                                    :rules="ifValid('entity_phones.phone_number')"
									:readonly="!hasScope('update')"
                                ></bc-phone-input>
                            </v-flex>
                            <v-flex xs12 v-if="!phone_type">
                                <bc-select
                                    v-model="entity_phone.phone_type"
                                    :label="`${$t('lang.phone_type')} *`"
                                    :items="PhoneTypes"
                                    :rules="ifValid('entity_phones.phone_type')"
									:readonly="!hasScope('update')"
                                >
                                    <template slot="item" slot-scope="data">
                                        {{ $t(`lang.${data.item}`) }}
                                    </template>
                                    <template slot="selection" slot-scope="data">
                                        {{ $t(`lang.${data.item}`) }}
                                    </template>
                                </bc-select>
                            </v-flex>
                        </v-layout>
                    </v-container>
                </v-form>
                <delete-confirmation-dialog
                    ref="delete_entity_phone"
                    :heading="$t('lang.delete_entity_phone')"
                    :special="entity_phone.notify == '1' ? $t('lang.delete_phone_for_notifications') : ''"
                    :message="$t('lang.confirm_delete_message')"
                    @onConfirm="deletePhone()"
                >
                    <v-form ref="delete_confirm_phone_form" @submit.prevent>
                        <bc-autocomplete
                            v-if="entity_phone.prime == '1' && entity_phone.entity_phones_id"
                            v-model="new_prime_entity_phones_id"
                            :label="`${$t('lang.new_primary_phone')} *`"
                            :items="availableItems"
                            item-value="entity_phones_id"
                            item-text="phone_number"
                            :rules="ifValid('entity_phones.new_prime_entity_phones_id')"
                        >
                            <template slot="item" slot-scope="data">
                                {{ data.item.phone_number_formatted ? data.item.phone_number_formatted : data.item.phone_number }}
                            </template>
                            <template slot="selection" slot-scope="data">
                                {{ data.item.phone_number_formatted ? data.item.phone_number_formatted : data.item.phone_number }}
                            </template>
                        </bc-autocomplete>
                    </v-form>
                </delete-confirmation-dialog>
            </v-card-text>
            <v-card-actions>
                <v-btn v-if="hasScope('delete') && entity_phone.entity_phones_id && availableItems.length > 0" color="error" @mouseup.left="$refs.delete_entity_phone.openDialog()">{{ $t('lang.delete') }}</v-btn>
                <v-spacer></v-spacer>
                <v-btn flat @mouseup.left="close()">{{ $t('lang.cancel') }}</v-btn>
                <v-btn
                    v-if="entity_phone.entity_phones_id ? hasScope('update') : hasScope('create')"
                    color="success"
                    @mouseup.left="savePhone()"
                >
                    {{$t('lang.save')}}
                </v-btn>
            </v-card-actions>
        </v-card>
    </bc-dialog>
</template>

<script>
import { mapState } from "vuex";
import _ from "lodash";

export default {
    props: {
        object_ref: {
            // object name matching first portion of api url i.e. people | me | customers
            type: String,
            required: true,
        },
        object_id: {
            type: [String, Number],
        },
        total_items: {
            // to decide if a new phone needs to be prime
            type: [Array, Object],
        },
        scope: {
            // scope from parent
            type: [String],
            default: null,
        },
        prime: {
            // whether to include a prime switch
            type: Boolean,
            default: true,
        },
        phone_type: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            entity_phone: {
                prime: "",
                object_ref: "",
                object_id: "",
                phone_number: "",
                country_iso_2: "US",
                phone_type: "",
            },
            entity_phone_dialog: false,
            PhoneTypes: ["home", "mobile", "work"],
            new_prime_entity_phones_id: null,
        };
    },
    computed: {
        ...mapState({
            rules_entity_phones: (state) => state.entity_phones.rules_entity_phones,
        }),
        availableItems() {
            let items = [];
            if (this.total_items && this.total_items.length > 1) {
                return this.total_items.filter((item) => item.entity_phones_id != this.entity_phone.entity_phones_id);
            }
            return items;
        },
        hasScope() {
            return (scope) => {
                return !this.scope || this.scope.includes(`${scope}`) || !this.entity_phone.entity_phones_id;
            };
        },
    },
    mounted: function() {
        // manually throw vue warning since dynamically setting entity_id as 'required: true/false' is not possible
        if (typeof this.object_ref == "undefined" && typeof this.object_id !== "undefined") {
            throw new Error('Missing required prop: "object_ref" OR "object_id""');
        }
    },
    methods: {
        close() {
            this.new_prime_entity_phones_id = null;
            this.entity_phone_dialog = false;
            this.$refs.delete_entity_phone.close();
            this.$store.commit("validationOff");
            this.$store.commit("toggleModalLoader", false);
        },
        savePhone() {
            this.$store.commit("validationOn");
            this.$nextTick(() => {
                if (this.validateForm("entity_phone_form")) {
                    this.$store.commit("toggleModalLoader", true);
                    //console.log(this.entity_phone);
                    let body = this.prepForm(this.entity_phone, this.rules_entity_phones);

                    this.$store
                        .dispatch("saveEntityPhone", {
                            body: body,
                        })
                        .then((data) => {
                            if (typeof data != "undefined" && data.code == "200") {
                                this.$emit("eventname", data.entity_phones);
                                this.close();
                            } else {
                                this.$store.commit("toggleModalLoader", false);
                            }
                        });
                }
            });
        },
        deletePhone() {
            this.$store.commit("validationOn");

            this.$nextTick(() => {
                if (this.validateForm("delete_confirm_phone_form")) {
                    this.$store
                        .dispatch("deleteEntityPhone", {
                            id: this.entity_phone.entity_phones_id,
                            new_prime_entity_phones_id: this.new_prime_entity_phones_id ? this.new_prime_entity_phones_id : null,
                        })
                        .then((data) => {
                            if (typeof data != "undefined" && data.code == "200") {
                                this.$emit("eventname", data.entity_phones);
                                this.close();
                            } else {
                                this.$store.commit("toggleModalLoader", false);
                            }
                            this.$store.commit("toggleModalLoader", false);
                        });
                }
            });
        },
        showPhone(item = null) {
            this.entity_phone = !item ? { object_ref: this.object_ref, object_id: this.object_id } : this.assignObj2(item, this.rules_entity_phones);

            //if (this.countries == null) this.getCountries();
            if (this.$refs.entity_phone) item ? this.$refs.entity_phone.resetValidation() : this.$refs.entity_phone.reset();

            this.$nextTick(() => {
                if (item) {
                    if (this.$refs.phoneInput) {
                        this.$refs.phoneInput.updateCurrentPhone({ phone_number: item.phone_number, code: item.country_iso_2 });
                    } else {
                        this.entity_phone_dialog = true;
                        this.entity_phone_dialog = false;
                        this.$nextTick(() => {
                            this.$refs.phoneInput.updateCurrentPhone({ phone_number: item.phone_number, code: item.country_iso_2 });
                        });
                    }
                } else {
                    if (this.$refs.phoneInput) this.$refs.phoneInput.updateCurrentPhone();
                }

                if (this.phone_type) this.entity_phone.phone_type = this.phone_type;
                this.entity_phone_dialog = true;
            });
        },
    },
};
</script>
